import React, { useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Combobox, toast } from '@node-space/ui-components'
import { useAssignCustomerFeeWalletMutation } from 'hooks/mutations/useAssignCustomerFeeWalletMutation'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { WalletType } from 'types/types'
import { floorWithPrecision } from 'utils/utils'
import { useCustomerFeeWalletForm } from '../hooks/useCustomerFeeWalletForm'

interface AddFeeWalletFormProps {
  currency: string
  allWallets: WalletType[]
  isFetchingAllWallets: boolean
  selectedWalletReference?: string
  isEdit?: boolean
}

export const AddFeeWalletForm = ({
  currency,
  allWallets,
  isFetchingAllWallets,
  selectedWalletReference,
  isEdit = false,
}: AddFeeWalletFormProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { formValues, errors, setValue } = useCustomerFeeWalletForm()

  const { mutate: assignFeeWallet, isPending: isAssigningFeeWallet } =
    useAssignCustomerFeeWalletMutation()

  useEffect(() => {
    if (selectedWalletReference) {
      setValue('walletReference', selectedWalletReference)
    }
  }, [selectedWalletReference, setValue])

  const currencyWallets = useMemo(
    () => allWallets?.filter(wallet => wallet?.currency?.code === currency) || [],
    [allWallets, currency]
  )

  const walletOptions = useMemo(
    () =>
      currencyWallets?.map(wallet => ({
        LeftElement: <Avatar name={wallet?.currency?.code?.toLowerCase()} size="small" />,
        value: wallet?.id?.toString(),
        leftText: wallet?.description,
        rightText: `${floorWithPrecision(wallet?.available, 2)} ${wallet?.currency?.code}`,
      })) || [],
    [currencyWallets]
  )

  const handleWalletSelection = (walletReference: string) => {
    setValue('walletReference', walletReference)

    if (!walletReference) return

    const selectedWallet = allWallets?.find(wallet => wallet?.id?.toString() === walletReference)
    if (!selectedWallet?.lsid) {
      return
    }

    assignFeeWallet(
      {
        destinationWalletId: selectedWallet?.lsid,
        currency: currency,
      },
      {
        onSuccess: () => {
          const successMessage = isEdit
            ? t('customers.feeSetup.successEdit')
            : t('customers.feeSetup.success')
          toast({ header: successMessage, variant: 'success' })
          queryClient.invalidateQueries({
            queryKey: reactQueryKeys.customerFeeWallets(),
          })
        },
        onError: () => {
          toast({ header: t('customers.feeSetup.error', { currency }), variant: 'error' })
        },
      }
    )
  }

  return (
    <Box flexCol gapY={8} width="full">
      <Combobox
        key={currency}
        id={`${currency}-fee-wallet-combobox`}
        label={`${currency} fee wallet`}
        optionalText={t('customers.feeSetup.optionalWallet')}
        items={walletOptions}
        defaultValue={formValues?.walletReference || ''}
        onChange={handleWalletSelection}
        selectPlaceholder={t('customers.feeSetup.selectWallet')}
        searchPlaceholder={t('customers.feeSetup.searchWallets', { currency })}
        loading={isFetchingAllWallets || isAssigningFeeWallet}
        loadingInline={isFetchingAllWallets || isAssigningFeeWallet}
        disabled={isFetchingAllWallets || isAssigningFeeWallet}
        errorText={
          errors.walletReference?.message ||
          (!walletOptions?.length && !isFetchingAllWallets
            ? t('customers.feeSetup.noWalletsAvailable', { currency })
            : undefined)
        }
      />
    </Box>
  )
}
